import { Box, Chip, styled } from '@mui/material';

import {
  useActiveTemplateMetadata,
  useIsApplying,
  usePreviewTemplate,
  usePreviewTemplateId,
  useTemplates,
} from '../../slices';

import type { FC } from 'react';

const PREFIX = 'TSMED-Templates';

export const templatesClasses = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  chipContainer: `${PREFIX}-chipContainer`,
  chip: `${PREFIX}-chip`,
  chipActive: `${PREFIX}-chipActive`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
})(({ theme: { spacing, palette } }) => ({
  borderRadius: '15px',
  width: '100%',
  padding: spacing(0, 2, 2, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1.5),
  alignItems: 'center',
  justifyContent: 'center',

  [`& .${templatesClasses.chipContainer}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing(1),
  },

  [`& .${templatesClasses.chip}`]: {
    backgroundColor: palette.mapBlue[800],
    color: palette.grey[600],
    borderRadius: '10px',
    padding: '20px',
    fontSize: '14px',
    fontWeight: 400,
    cursor: 'pointer',
    transition: 'all 0.2s ease',

    '&:hover': {
      backgroundColor: palette.mapBlue[600],
      borderColor: palette.blue[400],
    },

    '&.Mui-disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      color: palette.grey[700],
      backgroundColor: palette.mapBlue[900],
    },
  },

  [`& .${templatesClasses.chipActive}`]: {
    backgroundColor: palette.mapBlue[600],
    fontWeight: 600,
    color: palette.common.white,
  },
}));

export const Templates: FC = () => {
  const templates = useTemplates();
  const previewTemplateAction = usePreviewTemplate();
  const previewTemplateId = usePreviewTemplateId();
  const { id: activeTemplateId } = useActiveTemplateMetadata();
  const isApplying = useIsApplying();

  // Use preview template if set, otherwise fall back to active template
  const selectedTemplateId = previewTemplateId ?? activeTemplateId;

  const handleSelectTemplate = async (templateId: string) => {
    if (isApplying) {
      return; // Prevent selection during apply
    }

    try {
      await previewTemplateAction(templateId);
    } catch (error) {
      console.error('Failed to preview template:', error);
    }
  };

  return (
    <Root>
      <Box className={templatesClasses.chipContainer}>
        {templates.map(template => (
          <Chip
            key={template.id}
            label={template.name}
            onClick={() => handleSelectTemplate(template.id)}
            disabled={isApplying}
            className={`${templatesClasses.chip} ${
              selectedTemplateId === template.id
                ? templatesClasses.chipActive
                : ''
            }`}
          />
        ))}
      </Box>
    </Root>
  );
};
